import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { GUIDES, getGuide } from "./registry";
import useGuidanceProgress from "./useGuidanceProgress";
import JoyrideRunner from "./JoyrideRunner";
import HelpLauncher from "./HelpLauncher";

// ── Guidance orchestrator ─────────────────────────────────────────────────
// Mounted once in AppShell. Decides which guide (if any) to auto-show, renders
// the active guide and the always-on Help launcher, and wires up manual entry
// points:
//   - "amplehire:start-tour"   → core product tour (kept for ProfileDropdown)
//   - "amplehire:start-guide"  → { detail: { id } } start any guide by id
//
// Only one guide runs at a time. Auto-show rules:
//   - never when guidance is disabled, when a guide is already running,
//     or when the guide is snoozed / already seen
//   - first-login tours win over route coachmarks

// How long after mount / navigation before we try to show a guide — gives the
// shell and the page's anchors time to render.
const FIRST_LOGIN_DELAY_MS = 600;
const ROUTE_DELAY_MS = 500;

export default function GuidanceLayer() {
  const location = useLocation();
  const navigate = useNavigate();
  const { state, setStatus } = useGuidanceProgress();
  const [activeId, setActiveId] = useState(null);
  const firstLoginChecked = useRef(false);

  const isUnseen = useCallback((id) => !state.guides?.[id], [state.guides]);
  const isSnoozed = useCallback(
    (id) => {
      const until = state.snoozed?.[id];
      return !!until && until > Date.now();
    },
    [state.snoozed],
  );

  const start = useCallback(
    (id) => {
      const guide = getGuide(id);
      if (!guide) return;
      // Mark seen up front so a mid-guide reload won't re-trigger it.
      setStatus(id, "seen");
      // Route-scoped guide launched from elsewhere → go to its page first so
      // the anchor exists, then activate once the page has had time to render.
      const path = guide.trigger?.on === "route" ? guide.trigger.path : null;
      if (path && location.pathname !== path) {
        navigate(path);
        setTimeout(() => setActiveId(id), ROUTE_DELAY_MS);
      } else {
        setActiveId(id);
      }
    },
    [setStatus, navigate, location.pathname],
  );

  const finish = useCallback(
    (status) => {
      if (activeId) setStatus(activeId, status);
      setActiveId(null);
    },
    [activeId, setStatus],
  );

  // Manual entry points.
  useEffect(() => {
    const onStartTour = () => start("core-tour");
    const onStartGuide = (e) => {
      const id = e?.detail?.id;
      if (id) start(id);
    };
    window.addEventListener("amplehire:start-tour", onStartTour);
    window.addEventListener("amplehire:start-guide", onStartGuide);
    return () => {
      window.removeEventListener("amplehire:start-tour", onStartTour);
      window.removeEventListener("amplehire:start-guide", onStartGuide);
    };
  }, [start]);

  // First-login auto tour — runs once per shell mount; status guards reloads.
  useEffect(() => {
    if (firstLoginChecked.current) return undefined;
    firstLoginChecked.current = true;
    if (state.disabled || activeId) return undefined;
    const tour = GUIDES.find(
      (g) =>
        g.trigger?.on === "firstLogin" && isUnseen(g.id) && !isSnoozed(g.id),
    );
    if (!tour) return undefined;
    const t = setTimeout(() => start(tour.id), FIRST_LOGIN_DELAY_MS);
    return () => clearTimeout(t);
  }, [state.disabled, activeId, isUnseen, isSnoozed, start]);

  // Route coachmarks — only once a pending first-login tour is out of the way.
  useEffect(() => {
    if (state.disabled || activeId) return undefined;
    const pendingFirstLogin = GUIDES.some(
      (g) =>
        g.trigger?.on === "firstLogin" && isUnseen(g.id) && !isSnoozed(g.id),
    );
    if (pendingFirstLogin) return undefined;

    const guide = GUIDES.find(
      (g) =>
        g.trigger?.on === "route" &&
        g.trigger.path === location.pathname &&
        isUnseen(g.id) &&
        !isSnoozed(g.id),
    );
    if (!guide) return undefined;
    const t = setTimeout(() => start(guide.id), ROUTE_DELAY_MS);
    return () => clearTimeout(t);
  }, [location.pathname, state.disabled, activeId, isUnseen, isSnoozed, start]);

  const activeGuide = activeId ? getGuide(activeId) : null;

  return (
    <>
      <JoyrideRunner
        guide={activeGuide}
        onComplete={() => finish("completed")}
        onSkip={() => finish("skipped")}
      />
      <HelpLauncher onStartGuide={start} />
    </>
  );
}
